import { ICompassusNavigatorRepository } from "../../adapters/driven/repositories/compassusNavigatorRepository";
import { stripCompassusVirtualTodoTags } from "../../../util/compassusTodoTags";

function isGenerationStatusRunning(
  status: ICompassusGenerationStatusResponse | undefined | null
): boolean {
  const rawStatus = status?.generation_status ?? status?.status ?? null;
  if (typeof rawStatus === "string") return rawStatus === "running";
  if (!rawStatus || typeof rawStatus !== "object") return false;
  return (
    rawStatus.status === "running" || rawStatus.generation_status === "running"
  );
}

function buildRunningHospiceLlmRefreshResponse(
  status: ICompassusGenerationStatusResponse,
  patientId: number
): ICompassusPatientHospiceLlmRiskRefreshResponse {
  return {
    patient_id: status.patient_id ?? patientId,
    clinic_id: status.clinic_id ?? null,
    status: "running",
    hospice_rule_total_score: null,
    llm_risk_score: null,
    llm_evidence_json: null,
    llm_generated_at: null,
    generation_status: status.generation_status ?? status.status ?? null,
  };
}

export const CompassusNavigatorServiceImplFactory = ({
  compassusNavigatorRepository,
}: {
  compassusNavigatorRepository: ICompassusNavigatorRepository;
}) => {
  const getPatients: IGetCompassusPatients = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getPatientsAPI({
        clinic_id: clinicId,
        params: queryParams,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientIds: IGetCompassusPatientIds = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getPatientIdsAPI({
        clinic_id: clinicId,
        params: queryParams,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const exportNavigator: IExportCompassusNavigator = async ({
    clinicId,
    body,
  }) => {
    try {
      const response = await compassusNavigatorRepository.exportNavigatorAPI({
        clinic_id: clinicId,
        body,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getCounts: IGetCompassusCounts = async ({ clinicId, queryParams }) => {
    try {
      const response = await compassusNavigatorRepository.getCountsAPI({
        clinic_id: clinicId,
        params: queryParams,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPayerFilters: IGetCompassusPayerFilters = async ({ clinicId }) => {
    try {
      const response = await compassusNavigatorRepository.getPayerFiltersAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getMedicationFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getMedicationFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getAllergyFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getAllergyFiltersAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getConditionFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getConditionFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getLastVisitProviderFilters: IGetCompassusProviderFilters = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getLastVisitProviderFiltersAPI({
          clinic_id: clinicId,
          params: queryParams,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getNextVisitProviderFilters: IGetCompassusProviderFilters = async ({
    clinicId,
    queryParams,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getNextVisitProviderFiltersAPI({
          clinic_id: clinicId,
          params: queryParams,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getTodoTagFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getTodoTagFiltersAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getCustomTagFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getCustomTagFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getClinicalRiskScoreFilters: IGetCompassusClinicalRiskScoreFilters =
    async ({ clinicId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getClinicalRiskScoreFiltersAPI({
            clinic_id: clinicId,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const patchPatientTodo: IPatchCompassusPatientTodo = async ({
    clinicId,
    patientId,
    todoTags,
  }) => {
    try {
      const response = await compassusNavigatorRepository.patchPatientTodoAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        body: { tags: stripCompassusVirtualTodoTags(todoTags) },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const patchPatientHhJourneyStatus: IPatchCompassusPatientHhJourneyStatus =
    async ({ clinicId, patientId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.patchPatientHhJourneyStatusAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            body,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientHhJourneyStatus: IGetCompassusPatientHhJourneyStatus =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientHhJourneyStatusAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getPatientDataReview: IGetCompassusPatientDataReview = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientDataReviewAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientInsurance: IGetCompassusPatientInsurance = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientInsuranceAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      if (!response || typeof response.patient_id !== "number") {
        throw new Error("Unable to load patient insurance information");
      }
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientInsuranceRawResponse: IGetCompassusPatientInsuranceRawResponse =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientInsuranceRawResponseAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        if (
          !response ||
          typeof response.eligibility_record_id !== "number" ||
          !Object.prototype.hasOwnProperty.call(response, "response")
        ) {
          throw new Error("Unable to load the stored eligibility response");
        }
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getHhEligibilityWorklist: IGetCompassusHhEligibilityWorklist = async ({
    clinicId,
    params,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getHhEligibilityWorklistAPI({
          clinic_id: clinicId,
          params,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getHhNpWorklist: IGetCompassusHhNpWorklist = async ({
    clinicId,
    params,
  }) => {
    try {
      const response = await compassusNavigatorRepository.getHhNpWorklistAPI({
        clinic_id: clinicId,
        params,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getHhTechnicianWorklist: IGetCompassusHhTechnicianWorklist = async ({
    clinicId,
    params,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getHhTechnicianWorklistAPI({
          clinic_id: clinicId,
          params,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const patchHhNpUnlinkDraft: IPatchCompassusHhNpUnlinkDraft = async ({
    clinicId,
    assignmentId,
    ipvNoteId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.patchHhNpUnlinkDraftAPI({
          clinic_id: clinicId,
          assignment_id: assignmentId,
          body: { ipv_note_id: ipvNoteId },
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const rescheduleHhVisitAssignment: IRescheduleCompassusHhVisitAssignment =
    async ({ clinicId, patientId, assignmentId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.patchHhVisitScheduleAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            assignment_id: assignmentId,
            body,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const createHhAtRiskFollowUp: ICreateCompassusHhAtRiskFollowUp = async ({
    clinicId,
    patientId,
    body,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.postHhAtRiskFollowUpAPI({
          clinic_id: clinicId,
          patient_id: patientId,
          body,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const patchPatientCustomTags: IPatchCompassusPatientCustomTags = async ({
    clinicId,
    patientId,
    tags,
    hospitalizationRecords,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.postPatientCustomTagsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
          body: { tags, hospitalization_records: hospitalizationRecords },
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientCustomTags: IGetCompassusPatientCustomTags = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientCustomTagsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientAssociations: IGetCompassusPatientAssociations = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientAssociationsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  /**
   * Bulk to-do mutation — one HTTP request for any number of patients. See
   * `IBulkPatchCompassusPatientTodo` in the driven port for mode semantics.
   */
  const bulkPatchPatientTodo: IBulkPatchCompassusPatientTodo = async ({
    clinicId,
    patientIds,
    mode,
    todoTags,
  }) => {
    try {
      const body: ICompassusBulkPatchTodoRequest = {
        patient_ids: patientIds,
        mode,
      };
      // Backend ignores todo_tags when mode="clear", but omitting avoids
      // sending a superfluous field and matches the spec's "can be omitted".
      if (mode !== "clear") {
        body.todo_tags = stripCompassusVirtualTodoTags(todoTags ?? []);
      }
      const response =
        await compassusNavigatorRepository.bulkPatchPatientTodoAPI({
          clinic_id: clinicId,
          body,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const putWatchlist: IPutCompassusWatchlist = async ({ clinicId, items }) => {
    try {
      const response = await compassusNavigatorRepository.putWatchlistAPI({
        clinic_id: clinicId,
        body: { items },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const patchWatchlist: IPatchCompassusWatchlist = async ({
    clinicId,
    add,
    remove,
  }) => {
    try {
      const response = await compassusNavigatorRepository.patchWatchlistAPI({
        clinic_id: clinicId,
        body: { add, remove },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getAcuityLevelFilters: IGetCompassusStringFilters = async ({
    clinicId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getAcuityLevelFiltersAPI({
          clinic_id: clinicId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientAcuityLevelHistory: IGetCompassusPatientAcuityLevelHistory =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientAcuityLevelHistoryAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientPrognosisHistory: IGetCompassusPatientPrognosisHistory =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientPrognosisHistoryAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientFormHistory: IGetCompassusPatientFormHistory = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.getPatientFormHistoryAPI({
          clinic_id: clinicId,
          patient_id: patientId,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientHospiceEligibilityScore: IGetCompassusPatientHospiceEligibilityScore =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientHospiceEligibilityScoreAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientHospiceEligibilityScoreHistory: IGetCompassusPatientHospiceEligibilityScoreHistory =
    async ({ patientId, includeRubric, limit }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientHospiceEligibilityScoreHistoryAPI(
            {
              patient_id: patientId,
              include_rubric: includeRubric,
              limit,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getPatientHospiceEligibilityScoreLlmRefreshStatus: IGetCompassusPatientHospiceEligibilityScoreLlmRefreshStatus =
    async ({ patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientHospiceEligibilityScoreLlmRefreshStatusAPI(
            {
              patient_id: patientId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const refreshPatientHospiceEligibilityScoreLlm: IRefreshCompassusPatientHospiceEligibilityScoreLlm =
    async ({ patientId }) => {
      try {
        try {
          const status =
            await compassusNavigatorRepository.getPatientHospiceEligibilityScoreLlmRefreshStatusAPI(
              {
                patient_id: patientId,
              }
            );
          if (isGenerationStatusRunning(status)) {
            return {
              success: true,
              data: buildRunningHospiceLlmRefreshResponse(status, patientId),
            };
          }
        } catch (statusError) {
          console.warn(
            "[CompassusNavigatorService] Unable to check hospice LLM refresh status before POST:",
            statusError
          );
        }

        const response =
          await compassusNavigatorRepository.postPatientHospiceEligibilityScoreLlmRefreshAPI(
            {
              patient_id: patientId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  const getFormsSchema: IGetCompassusFormsSchema = async ({ clinicId }) => {
    try {
      const response = await compassusNavigatorRepository.getFormsSchemaAPI({
        clinic_id: clinicId,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientCommunicationNumbers: IGetCompassusPatientCommunicationNumbers =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientCommunicationNumbersAPI({
            clinic_id: clinicId,
            patient_id: patientId,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const linkPatientCommunicationNumber: ILinkCompassusPatientCommunicationNumber =
    async ({ clinicId, patientId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.postPatientCommunicationNumberAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            body,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const setPrimaryPatientCommunicationNumber: ISetPrimaryCompassusPatientCommunicationNumber =
    async ({ clinicId, patientId, phoneNumber }) => {
      try {
        const response =
          await compassusNavigatorRepository.putPatientCommunicationPrimaryNumberAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
              body: { phone_number: phoneNumber },
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const updatePatientCommunicationNumberLabel: IUpdateCompassusPatientCommunicationNumberLabel =
    async ({ clinicId, patientId, linkId, label }) => {
      try {
        const response =
          await compassusNavigatorRepository.patchPatientCommunicationNumberAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
              link_id: linkId,
              body: { label },
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const removePatientCommunicationNumber: IRemoveCompassusPatientCommunicationNumber =
    async ({ clinicId, patientId, linkId }) => {
      try {
        const response =
          await compassusNavigatorRepository.deletePatientCommunicationNumberAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
              link_id: linkId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getPatientCommunicationSms: IGetCompassusPatientCommunicationSms =
    async ({ clinicId, patientId, phoneNumber, limit }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientCommunicationSmsAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            phone_number: phoneNumber,
            limit,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getPatientCommunicationSmsThreads: IGetCompassusPatientCommunicationSmsThreads =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientCommunicationSmsThreadsAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const sendPatientCommunicationSms: ISendCompassusPatientCommunicationSms =
    async ({ clinicId, patientId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.postPatientCommunicationSmsAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            body,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const sendNpAppointmentSms: ISendCompassusNpAppointmentSms = async ({
    clinicId,
    patientId,
    body,
  }) => {
    try {
      const response =
        await compassusNavigatorRepository.postPatientNpAppointmentSmsAPI({
          clinic_id: clinicId,
          patient_id: patientId,
          body,
        });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: { message: (error as Error).message } };
    }
  };

  const getPatientCommunicationCalls: IGetCompassusPatientCommunicationCalls =
    async ({ clinicId, patientId, limit }) => {
      try {
        const response =
          await compassusNavigatorRepository.getPatientCommunicationCallsAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            limit,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const getPatientCommunicationCallToken: IGetCompassusPatientCommunicationCallToken =
    async ({ clinicId, patientId }) => {
      try {
        const response =
          await compassusNavigatorRepository.postPatientCommunicationCallTokenAPI(
            {
              clinic_id: clinicId,
              patient_id: patientId,
            }
          );
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  const startPatientCommunicationCall: IStartCompassusPatientCommunicationCall =
    async ({ clinicId, patientId, body }) => {
      try {
        const response =
          await compassusNavigatorRepository.postPatientCommunicationCallAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            body,
          });
        return { success: true, data: response };
      } catch (error) {
        return { success: false, error: { message: (error as Error).message } };
      }
    };

  return {
    getPatients,
    getPatientIds,
    exportNavigator,
    getCounts,
    getPayerFilters,
    getMedicationFilters,
    getAllergyFilters,
    getConditionFilters,
    getLastVisitProviderFilters,
    getNextVisitProviderFilters,
    getTodoTagFilters,
    getCustomTagFilters,
    getClinicalRiskScoreFilters,
    patchPatientTodo,
    getPatientHhJourneyStatus,
    patchPatientHhJourneyStatus,
    getPatientDataReview,
    getPatientInsurance,
    getPatientInsuranceRawResponse,
    getHhEligibilityWorklist,
    getHhNpWorklist,
    getHhTechnicianWorklist,
    patchHhNpUnlinkDraft,
    rescheduleHhVisitAssignment,
    createHhAtRiskFollowUp,
    patchPatientCustomTags,
    getPatientCustomTags,
    getPatientAssociations,
    bulkPatchPatientTodo,
    putWatchlist,
    patchWatchlist,
    getAcuityLevelFilters,
    getPatientAcuityLevelHistory,
    getPatientPrognosisHistory,
    getPatientFormHistory,
    getPatientHospiceEligibilityScore,
    getPatientHospiceEligibilityScoreHistory,
    getPatientHospiceEligibilityScoreLlmRefreshStatus,
    refreshPatientHospiceEligibilityScoreLlm,
    getFormsSchema,
    getPatientCommunicationNumbers,
    linkPatientCommunicationNumber,
    setPrimaryPatientCommunicationNumber,
    updatePatientCommunicationNumberLabel,
    removePatientCommunicationNumber,
    getPatientCommunicationSms,
    getPatientCommunicationSmsThreads,
    sendPatientCommunicationSms,
    sendNpAppointmentSms,
    getPatientCommunicationCalls,
    getPatientCommunicationCallToken,
    startPatientCommunicationCall,
  };
};
